import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSearchParams } from "react-router-dom";
import AdminLayout from '../layouts/AdminLayout';
import { Hotel } from '../types/hotel';
import { apiService } from '../services/api';
import Loader from '../components/Loader';
import HotelCard from '../components/HotelCard';
import ApprovalModal from '../components/ApprovalModal';
import AddHotelModal from '../components/AddHotelModal';
import {
  Plus,
  Search,
  AlertCircle,
  CheckCircle,
  Clock,
  XCircle,
  Hotel as HotelIcon,
} from 'lucide-react';
import { STATUS_MAP } from '../utils/helper';

// ---------- session storage helpers ----------
const SESSION_KEY = 'hotels_page_state';

const getDefaultDates = () => {
  const today = new Date();
  const pastDate = new Date();
  pastDate.setFullYear(today.getFullYear() - 1);
  const fmt = (d: Date) => d.toISOString().split('T')[0];
  return { startDate: fmt(pastDate), endDate: fmt(today) };
};

const loadState = () => {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    if (raw) return JSON.parse(raw);
  } catch { }
  return null;
};

const saveState = (state: object) => {
  try {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(state));
  } catch { }
};

const clearState = () => {
  try {
    sessionStorage.removeItem(SESSION_KEY);
  } catch { }
};
// ---------------------------------------------

const Hotels: React.FC = () => {

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const ownerId = searchParams.get('ownerId');

  // Restore saved state (if coming back from hotel detail page)
  const saved = loadState();
  const defaults = getDefaultDates();

  const [hotels, setHotels] = useState<Hotel[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState('');

  const [startDate, setCheckIn] = useState<string>(saved?.startDate ?? defaults.startDate);
  const [endDate, setCheckOut] = useState<string>(saved?.endDate ?? defaults.endDate);
  const [searchTerm, setSearchTerm] = useState<string>(saved?.searchTerm ?? '');
  const [filterStatus, setFilterStatus] = useState<'all' | 'pending' | 'approved' | 'rejected'>(
    saved?.filterStatus ?? 'all'
  );
  const [currentPage, setCurrentPage] = useState<number>(saved?.currentPage ?? 1);
  const [itemsPerPage, setItemsPerPage] = useState<number>(saved?.itemsPerPage ?? 9);

  const [selectedHotel, setSelectedHotel] = useState<Hotel | null>(null);
  const [modalType, setModalType] = useState<'approve' | 'reject' | null>(null);
  const [isPropertyDetailsModalOpen, setIsPropertyDetailsModalOpen] = useState(false);
  const [registeredOwner, setRegisteredOwner] = useState<any>(null);

  // ---------- persist state whenever any filter/page changes ----------
  useEffect(() => {
    saveState({ startDate, endDate, searchTerm, filterStatus, currentPage, itemsPerPage });
  }, [startDate, endDate, searchTerm, filterStatus, currentPage, itemsPerPage]);

  // ---------- fetch ----------
  const fetchHotels = async () => {
    setIsLoading(true);
    try {
      const params: any = { startDate, endDate };
      if (ownerId) params.ownerId = Number(ownerId);
      const response = await apiService.getHotels(params);
      const data = Array.isArray(response.data) ? response.data : [response.data];
      setHotels(data);
      setError('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch hotels');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchHotels();
  }, [ownerId, startDate, endDate]);

  // ---------- navigate to hotel detail (state is already persisted via the above effect) ----------
  const handleViewDetails = (hotelId: number) => {
    // state already saved by the useEffect above — just navigate
    navigate(`/hotels/${hotelId}`);
  };

  // ---------- filter ----------
  const filteredHotels = hotels.filter((hotel: any) => {
    if (ownerId && Number(hotel.ownerId) !== Number(ownerId)) return false;

    const matchesSearch =
      (hotel.propName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (hotel.address || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (hotel.ownerName || '').toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus =
      filterStatus === 'all' || STATUS_MAP[hotel.status || 1] === filterStatus;

    return matchesSearch && matchesStatus;
  });

  const indexOfLastHotel = currentPage * itemsPerPage;
  const indexOfFirstHotel = indexOfLastHotel - itemsPerPage;
  const currentHotels = filteredHotels.slice(indexOfFirstHotel, indexOfLastHotel);
  const totalPages = Math.ceil(filteredHotels.length / itemsPerPage);

  // ---------- actions ----------
  const handleApprove = async () => {
    if (!selectedHotel) return;
    setIsProcessing(true);
    try {
      await apiService.approveHotel(selectedHotel.id);
      setModalType(null);
      setSelectedHotel(null);
      await fetchHotels();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to approve hotel');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleReject = async (reason?: string) => {
    if (!selectedHotel) return;
    setIsProcessing(true);
    try {
      await apiService.rejectHotel(selectedHotel.id, reason || '');
      setModalType(null);
      setSelectedHotel(null);
      await fetchHotels();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reject hotel');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleAddHotel = async (formData: any) => {
    try {
      await apiService.createProperty(formData);
      await fetchHotels();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add hotel');
      throw err;
    }
  };

  const handlePropertyDetailsClose = async () => {
    setIsPropertyDetailsModalOpen(false);
    setRegisteredOwner(null);
    await fetchHotels();
  };

  // ---------- clear session when user navigates AWAY from hotels section ----------
  // (call this from sidebar links / logout — or use a route-change listener)
  // Simple approach: clear on unmount only if NOT going to a hotel detail page
  useEffect(() => {
    return () => {
      const leavingTo = window.location.pathname;
      const goingToDetail =
        leavingTo.startsWith('/hotels/') && leavingTo !== '/hotels';
      if (!goingToDetail) {
        clearState();
      }
    };
  }, []);

  // ---------- counts & tabs ----------
  const statusCounts = {
    all: hotels.length,
    approved: hotels.filter((h) => h.status === 2).length,
    pending: hotels.filter((h) => h.status === 1).length,
    rejected: hotels.filter((h) => h.status === 3).length,
  };

  const filterTabs = [
    { key: 'all', label: 'All', icon: HotelIcon, color: 'text-sage-600' },
    { key: 'approved', label: 'Approved', icon: CheckCircle, color: 'text-green-600' },
    { key: 'pending', label: 'Pending', icon: Clock, color: 'text-warm-600' },
    { key: 'rejected', label: 'Rejected', icon: XCircle, color: 'text-red-600' },
  ];

  // ---------- render ----------
  return (
    <AdminLayout>
      <div>

        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-serif font-bold text-sage-900">Hotels</h1>
            <p className="text-sm text-sage-500 mt-1">
              Manage all hotel and accommodation listings
            </p>
          </div>
          <button
            onClick={() => setIsPropertyDetailsModalOpen(true)}
            className="flex items-center gap-2 px-4 py-2.5 bg-brand-500 hover:bg-brand-600 text-white font-semibold text-sm rounded-xl transition-colors shadow-sm"
          >
            <Plus size={16} />
            Add Hotel
          </button>
        </div>

        {/* Date filters */}
        <div className="mb-6 bg-white border border-beige-300 rounded-xl p-4">
          <div className="flex flex-col md:flex-row md:items-center gap-3">

            <div className="relative flex-1">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-sage-400" />
              <input
                type="text"
                placeholder="Search by hotel name or city"
                value={searchTerm}
                onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                className="w-full pl-9 pr-3 py-2 border border-beige-300 rounded-lg text-sm"
              />
            </div>

            <input
              type="date"
              value={startDate}
              onChange={(e) => setCheckIn(e.target.value)}
              className="border border-beige-300 rounded-lg px-3 py-2 text-sm"
            />
            <input
              type="date"
              value={endDate}
              onChange={(e) => setCheckOut(e.target.value)}
              className="border border-beige-300 rounded-lg px-3 py-2 text-sm"
            />
            <button
              onClick={fetchHotels}
              className="px-4 py-2 bg-brand-600 text-white rounded-lg text-sm hover:bg-brand-700"
            >
              Apply
            </button>

          </div>
        </div>

        {/* Status tabs */}
        <div className="flex items-center gap-1 mb-6 bg-beige-200 p-1 rounded-xl overflow-x-auto">
          {filterTabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = filterStatus === tab.key;
            return (
              <button
                key={tab.key}
                onClick={() => { setFilterStatus(tab.key as any); setCurrentPage(1); }}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium ${isActive ? 'bg-white text-sage-900 shadow-sm' : 'text-sage-500 hover:text-sage-700'
                  }`}
              >
                <Icon size={14} className={isActive ? tab.color : 'text-sage-400'} />
                {tab.label}
                <span className="text-xs ml-1 px-1.5 py-0.5 rounded-full bg-beige-100 text-sage-700">
                  {statusCounts[tab.key as keyof typeof statusCounts]}
                </span>
              </button>
            );
          })}
        </div>

        {/* Error */}
        {error && (
          <div className="mb-6 p-3.5 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3">
            <AlertCircle size={18} className="text-red-500 mt-0.5" />
            <div>
              <p className="font-medium text-red-900 text-sm">Error loading hotels</p>
              <p className="text-red-700 text-sm mt-0.5">{error}</p>
            </div>
          </div>
        )}

        {/* Records per page */}
        <div className="flex items-center gap-2 mb-4">
          <select
            value={itemsPerPage}
            onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}
            className="border border-gray-300 rounded px-2 py-1 text-sm"
          >
            <option value={9}>9</option>
            <option value={24}>24</option>
            <option value={48}>48</option>
            <option value={99}>99</option>
          </select>
          <span className="text-sm font-medium text-green-700">records per page</span>
        </div>

        {/* Hotels grid */}
        {isLoading ? (
          <Loader fullScreen />
        ) : currentHotels.length === 0 ? (
          <div className="bg-white rounded-xl border border-beige-300 p-20 text-center shadow-sm">
            <div className="w-40 h-40 mx-auto mb-4 flex items-center justify-center">
              <img src="/no data.png" alt="No Hotels" className="w-full h-full object-contain" loading="eager" />
            </div>
            <p className="text-sage-900 text-lg font-semibold mb-1">No Hotels Found</p>
            <p className="text-sage-500 text-sm">No data available for the selected filters or date range.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {currentHotels.map((hotel) => (
              <HotelCard
                key={hotel.id}
                hotel={hotel}
                onViewDetails={() => navigate(`/hotels/${hotel.id}`)}
                onApprove={() => {
                  setSelectedHotel(hotel);
                  setModalType('approve');
                }}
                onReject={() => {
                  setSelectedHotel(hotel);
                  setModalType('reject');
                }}
                showActions={true}
              />
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center items-center gap-2 mt-8">
            <button
              onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
              disabled={currentPage === 1}
              className="px-3 py-1 border rounded-lg text-sm"
            >
              Prev
            </button>
            {Array.from({ length: totalPages }, (_, i) => (
              <button
                key={i}
                onClick={() => setCurrentPage(i + 1)}
                className={`px-3 py-1 border rounded-lg text-sm ${currentPage === i + 1 ? 'bg-brand-500 text-white' : 'bg-white'
                  }`}
              >
                {i + 1}
              </button>
            ))}
            <button
              onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
              disabled={currentPage === totalPages}
              className="px-3 py-1 border rounded-lg text-sm"
            >
              Next
            </button>
          </div>
        )}

        {/* Modals */}
        {selectedHotel && (
          <ApprovalModal
            isOpen={modalType !== null}
            hotelName={selectedHotel.propName}
            type={modalType || 'approve'}
            onConfirm={modalType === 'approve' ? handleApprove : handleReject}
            onCancel={() => setModalType(null)}
            isLoading={isProcessing}
          />
        )}

        <AddHotelModal
          isOpen={isPropertyDetailsModalOpen}
          onClose={handlePropertyDetailsClose}
          onSubmit={handleAddHotel}
        />

      </div>
    </AdminLayout>
  );
};

export default Hotels;